import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, IBoxProps } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import Label from './Label';
import Message from './Message';
import { Stack } from '../index';
import Select from '../Select';
import { Z_FG } from '../utilities/zIndex';

// we need the dangling comma because of how generics need to be in tsx files
// eslint-disable-next-line @typescript-eslint/comma-dangle
type SelectFormFieldProps<T = string,> = {
  message?: string | React.ReactNode;
  label?: string | React.ReactNode;
  accentColor?: string;
  items: Array<T>;
  value?: T;
  onChange: (selectedItem: T) => void;
  placeholder?: string;
  testID?: string;
  itemToString?: (item: T) => string;
  boxProps?: IBoxProps;
  disabled?: boolean;
  zIndex?: IBoxProps['zIndex'];
  zIndexWhenOpen?: IBoxProps['zIndex'];
  onIsOpenChange?: (isOpenNow: boolean) => void;
};

/**
 * A Select Form Component implementation atop `Select`.
 */
// we need the dangling comma because of how generics need to be in tsx files
// eslint-disable-next-line @typescript-eslint/comma-dangle
const SelectFormField = <T,>({
  message,
  label,
  accentColor,
  items,
  onChange,
  placeholder,
  value,
  testID,
  itemToString = item => String(item),
  boxProps,
  disabled,
  zIndex = Z_FG,
  zIndexWhenOpen = zIndex,
  onIsOpenChange,
}: SelectFormFieldProps<T>) => {
  const id = useMemo(() => `select-${uuidv4()}`, []);
  const [appliedZIndex, applyZIndex] = useState<IBoxProps['zIndex']>(zIndex);

  const handleOpenChange = (isOpenNow: boolean) => {
    applyZIndex(isOpenNow ? zIndexWhenOpen : zIndex);
    onIsOpenChange?.(isOpenNow);
  };

  return (
    <Box {...boxProps} zIndex={appliedZIndex}>
      <Stack>
        <Stack space={4} zIndex={appliedZIndex}>
          <Label
            label={label}
            testID={testID}
            id={id}
            color={disabled ? BrandColor.Ash : undefined}
          />
          <Select<T>
            onChange={({ selectedItem }) => onChange(selectedItem)}
            onIsOpenChange={handleOpenChange}
            items={items}
            placeholder={placeholder}
            selectedItem={value}
            appearance="select-form-field"
            testID={testID}
            itemToString={itemToString}
            disabled={disabled}
          />
        </Stack>
        <Message
          message={message}
          accentColor={disabled ? BrandColor.Ash : accentColor}
          testID={testID}
        />
      </Stack>
    </Box>
  );
};

SelectFormField.defaultProps = {
  label: undefined,
  accentColor: null,
  message: undefined,
  testID: null,
  placeholder: '',
  value: undefined,
};

export default SelectFormField;
